import useAsync from 'react-use/lib/useAsync';
import { useApi } from '@backstage/core-plugin-api';
import {
  Button,
  ButtonIcon,
  ButtonLink,
  Link,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@backstage/ui';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import EditIcon from '@material-ui/icons/Edit';
import GitHubIcon from '@material-ui/icons/GitHub';
import {
  AsyncCell,
  Column,
  DataTableCard,
  ExpandableText,
} from '@internal/plugin-shared-react';
import { sonarqubeApiRef } from '../../../api/sonarqube-api';
import { SonarQubeProject } from '../../../api/projects/types';
import styles from './SonarQubeServiceGroupPage.module.css';

const byProjectKey = <T extends { projectKey: string }>(items?: T[]) =>
  new Map((items ?? []).map(item => [item.projectKey, item]));

export interface ServiceGroupProjectsProps {
  serviceGroupId: string;
  onCreate: () => void;
  onUpdate: (projectKey: string) => void;
}

export const ServiceGroupProjects = ({
  serviceGroupId,
  onCreate,
  onUpdate,
}: ServiceGroupProjectsProps) => {
  const sonarqubeApi = useApi(sonarqubeApiRef);
  // Separate requests so each column fills in as soon as its own data arrives.
  const projects = useAsync(
    () => sonarqubeApi.getProjects(serviceGroupId),
    [sonarqubeApi, serviceGroupId],
  );
  const statuses = useAsync(
    async () =>
      byProjectKey(await sonarqubeApi.getProjectStatuses(serviceGroupId)),
    [sonarqubeApi, serviceGroupId],
  );
  const bindings = useAsync(
    async () =>
      byProjectKey(await sonarqubeApi.getProjectBindings(serviceGroupId)),
    [sonarqubeApi, serviceGroupId],
  );
  const descriptions = useAsync(
    async () =>
      byProjectKey(await sonarqubeApi.getProjectDescriptions(serviceGroupId)),
    [sonarqubeApi, serviceGroupId],
  );
  const qualityGates = useAsync(
    async () =>
      byProjectKey(await sonarqubeApi.getProjectQualityGates(serviceGroupId)),
    [sonarqubeApi, serviceGroupId],
  );

  const columns: Column<SonarQubeProject>[] = [
    {
      key: 'name',
      header: 'Project',
      maxWidth: "20rem",
      render: p => (
        <Link href={p.projectUrl} target="_blank" color="info">
          {p.name}
        </Link>
      ),
    },
    {
      key: 'description',
      header: 'Description',
      minWidth: "12rem",
      render: p => (
        <AsyncCell state={descriptions}>
          {() => {
            const description = descriptions.value?.get(p.key)?.description;
            return description ? <ExpandableText text={description} /> : '-';
          }}
        </AsyncCell>
      ),
    },
    {
      key: 'qualityGate',
      header: 'Quality gate',
      maxWidth: "10rem",
      render: p => (
        <AsyncCell state={qualityGates}>
          {() => qualityGates.value?.get(p.key)?.name ?? '-'}
        </AsyncCell>
      ),
    },
    {
      key: 'status',
      header: 'Last status',
      maxWidth: "8rem",
      render: p => (
        <AsyncCell state={statuses}>
          {() => {
            const status = statuses.value?.get(p.key);
            if (!status?.status) return '-';
            const button = (
              <Button
                size="small"
                variant="secondary"
                className={`${styles.status} ${
                  styles[`status${status.status}`]
                }`}
              >
                {status.status}
              </Button>
            );
            if (!status.message) return button;
            return (
              <TooltipTrigger>
                {button}
                <Tooltip>
                  <Text as="p">{status.message}</Text>
                </Tooltip>
              </TooltipTrigger>
            );
          }}
        </AsyncCell>
      ),
    },
    {
      key: 'github',
      header: '',
      maxWidth: "12rem",
      render: p => (
        <AsyncCell state={bindings}>
          {() => {
            const repositoryUrl = bindings.value?.get(p.key)?.repositoryUrl;
            return repositoryUrl ? (
              <ButtonLink
                variant="tertiary"
                iconStart={<GitHubIcon />}
                href={repositoryUrl}
                target="_blank"
              >
                View on GitHub
              </ButtonLink>
            ) : (
              '-'
            );
          }}
        </AsyncCell>
      ),
    },
    {
      key: 'update',
      header: '',
      maxWidth: "4rem",
      render: p => (
        <TooltipTrigger delay={300}>
          <ButtonIcon
            aria-label={`Update ${p.name}`}
            variant="tertiary"
            icon={<EditIcon fontSize="small" />}
            onPress={() => onUpdate(p.key)}
          />
          <Tooltip>
            <Text as="p">Update project</Text>
          </Tooltip>
        </TooltipTrigger>
      ),
    },
  ];

  const search = (p: SonarQubeProject, query: string) => {
    const q = query.toLowerCase();
    return [
      p.name,
      descriptions.value?.get(p.key)?.description ?? '',
      qualityGates.value?.get(p.key)?.name ?? '',
      statuses.value?.get(p.key)?.status ?? '',
      bindings.value?.get(p.key)?.repository ?? '',
    ].some(value => value.toLowerCase().includes(q));
  };

  return (
    <DataTableCard
      title="Projects"
      action={
        <Button iconStart={<AddCircleIcon />} onClick={onCreate}>
          Create new project
        </Button>
      }
      columns={columns}
      rows={projects.value ?? []}
      rowKey={p => p.key}
      searchPlaceholder="Search for projects..."
      searchMatch={search}
      pageSize={10}
      loading={projects.loading}
    />
  );
};